import { useState } from 'react';
import useFeatureToggle from 'framework/featureToggle';
import { Features } from 'infrastructure/featureToggle';
import { useRecruitmentApi } from 'infrastructure/api';
import { useGetData, useSingletonPromise } from 'framework/utils';
import { useMessageService, ErrorMessage } from 'framework/ui/Message';
import { usePositionService } from 'domain/shared/employee';
import { useTechnologyService } from 'domain/shared/career';
import {
  ConversionPositionSlotFormData,
  CandidateHiringContextVacancy,
  CandidateHiringContextResponse,
  mapConversionPositionSlotToFormData,
} from './conversionPositionSlotFormData';
import { CANDIDATE_RESOURCE_URL } from 'domain/shared/candidate';

type ConversionPositionSlotLoadState = {
  candidateId: Nullable<string>;
  positionSlot: Nullable<ConversionPositionSlotFormData>;
  positionSlotsEnabled: boolean;
};

const useConversionPositionSlot = (candidateId?: Nullable<StringOrNumber>) => {
  const { isFeatureEnabled } = useFeatureToggle();
  const { sendGetRequest } = useRecruitmentApi();
  const { findAll: findAllPositions } = usePositionService();
  const { findAll: findAllTechnologies } = useTechnologyService();
  const { addMessage } = useMessageService();
  const positionSlotsEnabled = isFeatureEnabled(Features.POSITION_SLOTS);
  const normalizedCandidateId = candidateId ? String(candidateId) : null;
  const isEnabled = Boolean(normalizedCandidateId) && positionSlotsEnabled;

  const loadHiringContextVacancy = useSingletonPromise<string, Nullable<CandidateHiringContextVacancy>>(
    'getCandidateHiringContext',
    async (id: string) => {
      const response = await sendGetRequest(`${CANDIDATE_RESOURCE_URL}/${id}/hiring-context`);
      const { vacancy } = (await response.json()) as CandidateHiringContextResponse;

      return vacancy ?? null;
    },
  );

  const [loadState, setLoadState] = useState<ConversionPositionSlotLoadState>({
    candidateId: normalizedCandidateId,
    positionSlot: null,
    positionSlotsEnabled,
  });

  const buildLoadState = (positionSlot: Nullable<ConversionPositionSlotFormData>): ConversionPositionSlotLoadState => ({
    candidateId: normalizedCandidateId,
    positionSlot,
    positionSlotsEnabled,
  });

  const loadPositionSlot = async () => {
    if (!isEnabled || !normalizedCandidateId) {
      return buildLoadState(null);
    }

    try {
      const vacancy = await loadHiringContextVacancy(normalizedCandidateId);
      const positionSlot = vacancy
        ? mapConversionPositionSlotToFormData(vacancy, await findAllPositions(), await findAllTechnologies())
        : null;

      return buildLoadState(positionSlot);
    } catch {
      addMessage(
        new ErrorMessage('Could not load the position slot for this candidate. Please fill in the HR info manually.'),
      );

      return buildLoadState(null);
    }
  };

  const { loading } = useGetData(
    loadPositionSlot,
    setLoadState,
    isEnabled,
    normalizedCandidateId,
    positionSlotsEnabled,
  );

  const isLoadStateCurrent =
    loadState.candidateId === normalizedCandidateId && loadState.positionSlotsEnabled === positionSlotsEnabled;

  return {
    positionSlot: isEnabled && isLoadStateCurrent ? loadState.positionSlot : null,
    isLoading: isEnabled && (loading || !isLoadStateCurrent),
  };
};

export default useConversionPositionSlot;
